import { resolveNestedList, useCacheOperations } from "@/composables/cache";
import { useLogger } from "@/composables/logger";
import { Order_By } from "gql/api";
import type { ModelsResolver } from "@/models/resolvers";
import { revisionDefaultFields } from "@/models/resolvers";

const { info: logInfo } = useLogger("SCENARIOS_RESOLVER");

export default {
  queries: {
    scenarios: {
      author: (parent, args, cache, info) => {
        return parent.journal_author_id
          ? { __typename: "users", id: parent.journal_author_id }
          : null;
      },
      correlations: (parent, args, cache, info) => {
        return parent.correlations_id
          ? { __typename: "scenario_correlations", id: parent.correlations_id }
          : null;
      },
      equipment: (parent, args, cache, info) => {
        return parent.scenario_equipment_id
          ? {
              __typename: "scenario_equipment",
              id: parent.scenario_equipment_id
            }
          : null;
      },
      parameters: (parent, args, cache, info) => {
        return parent.parameters_id
          ? { __typename: "scenario_parameters", id: parent.parameters_id }
          : null;
      },
      performance_evaluations(parent, args, cache, info) {
        return resolveNestedList(
          parent.id,
          cache,
          "scenarios",
          cache.keyOfField("performance_evaluations", args) ||
            "performance_evaluations",
          "performance_evaluations"
        );
      },
      sizing: (parent, args, cache, info) => {
        return parent.sizing_id
          ? { __typename: "sizings", id: parent.sizing_id }
          : null;
      },
      well_properties: (parent, args, cache, info) => {
        return parent.well_properties_id
          ? {
              __typename: "scenario_well_properties",
              id: parent.well_properties_id
            }
          : null;
      }
    }
  },
  optimistic: {
    insert_revisions_scenarios_revisions: (vars, cache, info) => {
      logInfo("Optimistic", { vars, cache, info });
      const affectedRows = vars.objects.length;

      return {
        __typename: "revisions_scenarios_revisions_mutation_response",
        affected_rows: affectedRows,
        returning: vars.objects.map(obj => ({
          id: obj.id,
          __typename: "revisions_scenarios_revisions",
          scenarios_view: {
            __typename: "scenarios",
            author: null,
            correlations: null,
            equipment: null,
            parameters: null,
            well_properties: null,
            ...obj,
            ...revisionDefaultFields(obj)
          }
        }))
      };
    }
  },
  updates: {
    insert_revisions_scenarios_revisions: (result, args, cache, info) => {
      logInfo("MUTATION UPDATE", { result, args, cache, info });
      args.objects.forEach(obj => {
        const { handleCacheUpdate } = useCacheOperations({
          cache,
          object: obj,
          typename: "scenarios"
        });

        if (info.optimistic) {
          handleCacheUpdate([
            {
              args: {
                where: { title: { _eq: "_offline_" } }
              }
            }
          ]);
        }

        handleCacheUpdate(
          [
            {
              args: {
                where: {
                  journal_deleted_at: { _is_null: true },
                  sizing: { journal_deleted_at: { _is_null: true } }
                },
                order_by: [{ journal_created_at: Order_By.Asc }]
              },
              relatedCacheKey: cache.keyOfEntity({
                __typename: "sizings",
                id: obj.sizing_id
              }),
              relatedField: "scenarios"
            }
          ],
          false
        );
      });
    }
  }
} as ModelsResolver;
